import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ScheduledReport } from '../../../models/api/reports/all-reports-response';
import { Action } from '../../../models/core/action';
import { CasePipe } from '../../../pipes/case.pipe';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { TagComponent } from '../../core/labels/tag.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { FormatPipe } from '../../../pipes/format.pipe';

@Component({
  selector: 'app-scheduled-reports',
  imports: [
    TableComponent,
    ColumnComponent,
    MenuComponent,
    CommonModule,
    ActionButtonComponent,
    TagComponent,
    LinkLabelComponent,
    CasePipe,
    FormatPipe,
  ],
  template: `
    <div>
      <div class="tw-pt-2">
        <app-table
          [dataSource]="scheduledReports()"
          recordLabel="scheduled report"
          dataKey="requestId"
          scrollHeight="2000px"
          [stripedRows]="false"
          [loading]="loading()"
          [showRefresh]="true"
          sortField="nextRunDate"
          [sortOrder]="-1"
          (refresh)="refresh()"
          [showFilterButton]="false"
          [showFilter]="false"
          [ariaTableLabel]="'Scheduled report'"
          emptyIcon="fa-file-lines fa-light"
          emptyMessage="Get started by creating a new report or scheduling an existing one."
        >
          <app-column
            name="reportName"
            label="Name"
            filterType="select"
            [wrap]="true"
            minWidth="320px"
            maxWidth="420px"
          >
            <ng-template let-data>
              <div
                class="tw-flex tw-items-center tw-gap-2 tw-max-w-full tw-break-words tw-whitespace-normal"
              >
                <app-link-label
                  [href]="getReportDetailHref(data)"
                  (click)="viewReportDetails(data)"
                  style="cursor: pointer;"
                >
                  {{ data.reportName }}
                </app-link-label>
              </div>
            </ng-template>
          </app-column>

          <app-column name="filterName" label="Filter" filterType="select">
            <ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.filterName }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="contract"
            label="Client"
            filterType="select"
            filterLabelName="clientName"
            filterLabelCase="titlecase"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                @if (data.contract && data.clientName) {
                  {{ data.contract }} -
                  {{ data.clientName | case: 'titlecase' }}
                }
              </div>
            </ng-template>
          </app-column>

          <app-column
            name="lastRunDate"
            label="Last run"
            format="dt"
            filterType="date"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.lastRunDate | format: 'dt' }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="nextRunDate"
            label="Next run"
            format="dt"
            filterType="date"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.nextRunDate | format: 'dt' }}
              </div>
            </ng-template>
          </app-column>

          <app-column
            name="requestFrequency"
            label="Frequency"
            filterType="select"
          >
            <ng-template #value let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                @if (getFrequencyLabel(data.requestFrequency) === 'Once') {
                  <app-tag type="Grey">{{
                    getFrequencyLabel(data.requestFrequency)
                  }}</app-tag>
                } @else {
                  <app-tag type="Purple">{{
                    getFrequencyLabel(data.requestFrequency)
                  }}</app-tag>
                }
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="actions"
            label="Actions"
            classes=""
            [frozen]="true"
            [sort]="false"
          >
            <ng-template let-data>
              <app-action-button
                [ariaLabel]="'More actions for ' + data.reportName"
                [menu]="rowMenu"
                [row]="data"
                (click)="rowMenu.toggle($event, data)"
              />
            </ng-template>
          </app-column>
        </app-table>
        <app-menu
          [items]="rowActions"
          #rowMenu
          (click)="onAction($event, rowMenu.data)"
        />
      </div>
      @if (scheduledReports().length > 0) {
        <p class="tw-text-sm tw-italic tw-text-gray-600 tw-mt-4">
          Your scheduled reports will appear here. Use this tab to view details,
          update delivery settings, or remove schedules you no longer need.
        </p>
      }
    </div>
  `,
  styles: ``,
})
export class ScheduledReportsComponent extends BaseComponent implements OnInit {
  private readonly reportsService = inject(ReportsService);
  private readonly snackbar = inject(Snackbar);
  protected override router = inject(Router);

  rowActions: Action[] = [
    { label: 'View details', key: 'view' },
    { label: 'Edit delivery details', key: 'edit-delivery' },
    { label: 'Delete schedule', key: 'delete' },
  ];

  readonly scheduledReports = this.reportsService.scheduledReports;
  readonly loading = this.reportsService.scheduledLoading;

  async ngOnInit() {
    await this.reportsService.getReports();
  }

  getFrequencyLabel(frequency: string): string {
    const frequencyMap: Record<string, string> = {
      N: 'Once',
      D: 'Daily',
      W: 'Weekly',
      M: 'Monthly',
      Q: 'Quarterly',
      S: 'Semi-Annual',
      A: 'Annual',
    };

    return frequencyMap[frequency] || frequency;
  }

  getReportDetailHref(report: ScheduledReport): string {
    return this.reportsService.getDetailUrl(report, 'scheduled');
  }

  viewReportDetails(report: ScheduledReport) {
    this.reportsService.navigateToDetail(report, 'scheduled');
  }

  onAction(action: Action, report: ScheduledReport) {
    if (action.key === 'view') {
      this.viewReportDetails(report);
    } else if (action.key === 'edit-delivery') {
      this.editDeliveryDetails(report);
    } else if (action.key === 'delete') {
      const nextRunDate = new Date(report.nextRunDate)
        .toLocaleString()
        .replace(',', '');
      this.confirm.show({
        label: 'Delete scheduled report',
        message: `Are you sure you want to delete the schedule for ${
          report.reportName + ` (Next Run: ${nextRunDate})`
        }? The report will be removed from the Scheduled Reports grid.`,
        width: '35rem',
        data: report,
        confirm: (data) => this.deleteReport(data),
      });
    }
  }

  async editDeliveryDetails(report: ScheduledReport) {
    const hasRunOnce = !!report.lastRunDate;

    await this.reportsService.loadScheduledRequestForEditing(
      report.requestId!,
      'JURIS',
      'CLAIM',
      report.contract,
      hasRunOnce,
    );

    this.reportsService.setMode('edit-scheduled');
    this.router.navigate(['/reports/edit-report'], {
      queryParams: { step: 'delivery' },
    });
  }

  async deleteReport(report: ScheduledReport) {
    const response = await this.reportsService.deleteScheduledReport(
      report.requestId,
    );
    if (response.success) {
      this.snackbar.show('Scheduled report deleted', 'Success');
    } else {
      this.snackbar.show(
        response.message,
        'Error',
        'fa-light fa-circle-exclamation',
        5000,
      );
    }
  }

  async refresh() {
    await this.reportsService.getReports(true);
  }
}
